import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Upload, AlertCircle, CheckCircle2, ArrowLeft, File, CreditCard } from 'lucide-react';
import { PendingUpload } from '../types/uploads';

export default function FileUpload() {
  const { uploadId } = useParams<{ uploadId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [upload, setUpload] = useState<PendingUpload | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [feeVerified, setFeeVerified] = useState<boolean | null>(null);
  const [retryLoading, setRetryLoading] = useState(false);

  // Load upload + verify fee in a single sequence so we can check upload.metadata.fee_id
  useEffect(() => {
    if (!uploadId) return;
    initUpload();
  }, [uploadId, searchParams]);

  // Load upload then verify fee. Checks fee_id from: URL → localStorage → upload.metadata.fee_id.
  // Not gated on fee_paid URL param so "Continue" links without params still work after payment.
  const initUpload = async () => {
    if (!uploadId) return;
    try {
      const { data, error } = await supabase
        .from('pending_uploads')
        .select('*')
        .eq('id', uploadId)
        .single();
      if (error) throw error;
      setUpload(data as unknown as PendingUpload);

      // Bonus content — no fee required
      if (searchParams.get('bonus') === 'true') {
        setFeeVerified(true);
        return;
      }

      // Resolve fee_id from multiple sources (most → least durable)
      let feeId: string | null = searchParams.get('fee_id');
      if (!feeId || feeId === '{FEE_ID}') feeId = localStorage.getItem(`pending_fee_${uploadId}`);
      if (!feeId) {
        const metaFeeId = (data.metadata as Record<string, unknown>)?.fee_id;
        feeId = typeof metaFeeId === 'string' ? metaFeeId : null;
      }
      // Fallback: use upload_fee_id from the DB row (set by webhook)
      if (!feeId && data.upload_fee_id) {
        feeId = data.upload_fee_id;
      }

      if (feeId) {
        verifyFee(feeId);
      } else {
        setFeeVerified(false);
      }
    } catch (err: any) {
      console.error('Error initializing upload:', err);
      toast.error('Failed to load upload details');
    }
  };

  // Polls DB up to maxAttempts×2 s for the webhook to mark the fee paid.
  const verifyFee = async (feeId: string, attempt = 0) => {
    const maxAttempts = 6;
    try {
      const { data, error } = await supabase
        .from('content_upload_fees')
        .select('id, paid')
        .eq('id', feeId)
        .single();

      if (!error && data?.paid) {
        // Keep localStorage entry so re-visiting the page still verifies without re-paying
        setFeeVerified(true);
        return;
      }

      if (!error && data && !data.paid && attempt < maxAttempts) {
        setTimeout(() => verifyFee(feeId, attempt + 1), 2000);
        return;
      }

      setFeeVerified(false);
    } catch {
      setFeeVerified(false);
    }
  };

  const handleRetryPayment = async () => {
    if (!upload || !uploadId) return;
    setRetryLoading(true);
    try {
      // Map pending_upload content_type → fee_type (same logic as ContentUpload.tsx)
      const feeType = upload.content_type === 'tv_show' ? 'episode' : upload.content_type;
      const durationMinutes = upload.metadata.runtime_claimed
        ? Math.round(upload.metadata.runtime_claimed / 60)
        : null;

      const successUrl = `${window.location.origin}/content/upload/${uploadId}?fee_paid=true&fee_id={FEE_ID}`;
      const cancelUrl = `${window.location.origin}/content/upload/${uploadId}`;

      const { data, error } = await supabase.functions.invoke('create-upload-fee-checkout', {
        body: {
          publisher_id: upload.publisher_id,
          work_id: upload.work_id ?? null,
          episode_id: upload.episode_id ?? null,
          content_type: feeType,
          duration_minutes: durationMinutes,
          success_url: successUrl,
          cancel_url: cancelUrl,
          pending_upload_id: uploadId,
        },
      });

      if (error) throw error;

      if (data?.free || data?.checkout_url === undefined) {
        // Edge case: content turned out to be free
        setFeeVerified(true);
        return;
      }

      if (data?.fee_id) {
        localStorage.setItem(`pending_fee_${uploadId}`, data.fee_id);
        // Persist fee_id into upload metadata so it survives localStorage clears
        const currentMeta = (upload?.metadata as unknown as Record<string, unknown>) ?? {};
        supabase.from('pending_uploads')
          .update({ metadata: { ...currentMeta, fee_id: data.fee_id } })
          .eq('id', uploadId)
          .then(() => {});
      }
      window.location.href = data.checkout_url;
    } catch (err: any) {
      toast.error(err.message || 'Failed to start payment. Please try again.');
    } finally {
      setRetryLoading(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-matroska'];
    if (!allowedTypes.includes(selectedFile.type)) {
      setError('Invalid file type. Please upload MP4, MOV, or MKV files.');
      return;
    }

    // Validate file size (max 50GB for now)
    const maxSize = 50 * 1024 * 1024 * 1024; // 50GB
    if (selectedFile.size > maxSize) {
      setError('File size exceeds maximum allowed size of 50GB');
      return;
    }

    setFile(selectedFile);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file || !upload || !uploadId) return;

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned PUT URL from edge function
      const { data: presign, error: presignError } = await supabase.functions.invoke('r2-presign-upload', {
        body: {
          pending_upload_id: uploadId,
          filename: file.name,
          content_type: file.type || 'video/mp4',
        },
      });

      if (presignError) throw presignError;
      if (!presign?.signed_url) throw new Error('No presigned URL returned');

      // Step 2: Upload directly to R2 via XHR (enables progress tracking)
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presign.signed_url);
        xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');

        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            setUploadProgress(Math.round((event.loaded / event.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed: HTTP ${xhr.status}`));
          }
        };

        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.ontimeout = () => reject(new Error('Upload timed out'));
        xhr.send(file);
      });

      // Step 3: Confirm upload — edge function verifies file in R2, sets status to human_review
      const { data: confirm, error: confirmError } = await supabase.functions.invoke('r2-confirm-upload', {
        body: { pending_upload_id: uploadId },
      });

      if (confirmError) throw confirmError;
      if (!confirm?.success) throw new Error('Upload confirmation failed');

      toast.success('Upload complete! Your content is now under review.');
      setTimeout(() => navigate('/content/assets'), 2000);

    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed. Please try again.');
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (!upload) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-muted-foreground">Loading upload details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Payment gate — block upload until fee is verified
  if (feeVerified === false) {
    return (
      <div className="container mx-auto py-8 max-w-2xl">
        <Button variant="ghost" className="mb-6" onClick={() => navigate('/content/uploads')}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Uploads
        </Button>
        <div className="flex flex-col items-center justify-center text-center py-16 space-y-4">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <CreditCard className="h-8 w-8 text-destructive" />
          </div>
          <h2 className="text-2xl font-bold">Payment Required</h2>
          <p className="text-muted-foreground max-w-md">
            Upload fee payment hasn't been confirmed yet. Complete the payment to proceed with
            your upload.
          </p>
          <div className="flex gap-3">
            <Button onClick={handleRetryPayment} disabled={retryLoading}>
              <CreditCard className="mr-2 h-4 w-4" />
              {retryLoading ? 'Starting payment...' : 'Pay Upload Fee'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/content/uploads')}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Still verifying (may be polling for webhook) — show loading
  if (feeVerified === null) {
    return (
      <div className="container mx-auto py-8 max-w-4xl">
        <div className="flex items-center justify-center h-64">
          <div className="text-center space-y-2">
            <p className="text-muted-foreground">Confirming payment...</p>
            <p className="text-xs text-muted-foreground">This may take a few seconds</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <Button
        variant="ghost"
        className="mb-6"
        onClick={() => navigate('/content/uploads')}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Uploads
      </Button>

      <div className="mb-8">
        <h1 className="text-3xl font-bold">Upload Video File</h1>
        <p className="text-muted-foreground mt-2">
          Upload your video file for: <strong>{upload.metadata.title}</strong>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>File Upload</CardTitle>
          <CardDescription>
            Supported formats: MP4, MOV, MKV • Maximum size: 50GB
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!file && !uploading && (
            <div className="border-2 border-dashed rounded-lg p-12 text-center hover:border-primary transition-colors">
              <label htmlFor="file-upload" className="cursor-pointer">
                <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
                <p className="mt-4 text-lg font-medium">
                  Drag and drop your video file here
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                  or click to browse your files
                </p>
                <input
                  id="file-upload"
                  type="file"
                  accept="video/mp4,video/quicktime,video/x-matroska"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>
          )}

          {file && !uploading && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 border rounded-lg">
                <File className="h-10 w-10 text-muted-foreground" />
                <div className="flex-1">
                  <p className="font-medium">{file.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <CheckCircle2 className="h-6 w-6 text-green-500" />
              </div>

              <div className="flex gap-4">
                <Button onClick={handleUpload} className="flex-1">
                  <Upload className="mr-2 h-4 w-4" />
                  Start Upload
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setFile(null);
                    setError(null);
                  }}
                >
                  Change File
                </Button>
              </div>
            </div>
          )}

          {uploading && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 border rounded-lg">
                <File className="h-10 w-10 text-muted-foreground" />
                <div className="flex-1">
                  <p className="font-medium">{file?.name}</p>
                  <p className="text-sm text-muted-foreground">
                    Uploading... {uploadProgress}%
                  </p>
                </div>
              </div>

              <Progress value={uploadProgress} className="w-full" />

              <p className="text-sm text-muted-foreground text-center">
                Please don't close this page while uploading
              </p>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 p-4 bg-destructive/10 text-destructive rounded-lg">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Content Metadata</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Type</p>
              <Badge variant="secondary">{upload.content_type}</Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Rating</p>
              <Badge variant="secondary">{upload.metadata.rating}</Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Language</p>
              <Badge variant="secondary">{upload.metadata.language}</Badge>
            </div>
            {upload.metadata.runtime_claimed && (
              <div>
                <p className="text-sm text-muted-foreground">Claimed Runtime</p>
                <Badge variant="secondary">
                  {Math.floor(upload.metadata.runtime_claimed / 60)} minutes
                </Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
